import React from 'react'
import _map from 'lodash/map'
import _isEqual from 'lodash/isEqual'
import _sortBy from 'lodash/sortBy'
import _reduce from 'lodash/reduce'
import SortableTable from '../../../_library/SortableTable'
import { getTableColumns, makeRunningTotalLabel } from '../../../utils/sortableTableUtils'
import {
  formatDay,
  createFixedFloatNormalizer,
  currencyNormalizerCreator,
} from '../../../../_common/core/validation/normalizers'
import _get from 'lodash/get'

export default class SalesByPromoCode extends React.PureComponent {
  constructor(props) {
    super(props)
    this.tableColumns = this.getMainTableColumns(props.event)
    this.detailsTableColumns = this.getDetailsTableColumns(props.event)
    this.footbarColumns = this.getFooterColumns(props.event)

    this.state = {
      sortBy: { column: 'discountCode', asc: true },
      rows: null,
      detailsRows: [],
    }
  }

  getMainTableColumns(event) {
    return getTableColumns(
      [
        { key: 'discountCode', label: 'Promo Code' },
        { key: 'ticketType', label: 'Ticket Type' },
        {
          key: 'cost',
          label: 'Price (excl. Fees)',
          disableNormalizerOnCopy: true,
          normalizer: value =>
            currencyNormalizerCreator(getActiveCurrencySymbol(event))(
              createFixedFloatNormalizer(2)(parseFloat(value)),
            ),
        },
        { key: 'numSales', label: 'No. of Sales' },
        {
          key: 'revenue',
          label: 'Revenue',
          disableNormalizerOnCopy: true,
          normalizer: value =>
            currencyNormalizerCreator(getActiveCurrencySymbol(event))(
              createFixedFloatNormalizer(2)(parseFloat(value)),
            ),
        },
        {
          key: 'grossRevenue',
          label: 'Gross Revenue',
          disableNormalizerOnCopy: true,
          normalizer: value =>
            currencyNormalizerCreator(getActiveCurrencySymbol(event))(
              createFixedFloatNormalizer(2)(parseFloat(value)),
            ),
        },
      ],
      getActiveCurrencySymbol(event),
    )
  }

  getDetailsTableColumns(event) {
    return getTableColumns([
      {
        key: 'datetime',
        label: 'Order Date',
        className: 'detail-cell',
        normalizer: formatDay,
      },
      {
        key: 'numSales',
        label: 'No. of Sales',
        className: 'detail-cell',
      },
      {
        key: 'numSalesTotal',
        label: makeRunningTotalLabel('No. of Sales').value,
        labelComponent: makeRunningTotalLabel('No. of Sales').component,
        className: 'detail-cell',
      },
      {
        key: 'revenue',
        label: 'Revenue',
        className: 'detail-cell',
        disableNormalizerOnCopy: true,
        normalizer: value =>
          currencyNormalizerCreator(getActiveCurrencySymbol(event))(
            createFixedFloatNormalizer(2)(parseFloat(value)),
          ),
      },
      {
        key: 'revenueTotal',
        label: makeRunningTotalLabel('Revenue').value,
        labelComponent: makeRunningTotalLabel('Revenue').component,
        className: 'detail-cell',
        disableNormalizerOnCopy: true,
        normalizer: value =>
          currencyNormalizerCreator(getActiveCurrencySymbol(event))(
            createFixedFloatNormalizer(2)(parseFloat(value)),
          ),
      },
      {
        key: 'discountedAmount',
        label: 'Discounted Amount',
        className: 'detail-cell',
        disableNormalizerOnCopy: true,
        normalizer: value =>
          currencyNormalizerCreator(getActiveCurrencySymbol(event))(
            createFixedFloatNormalizer(2)(parseFloat(value)),
          ),
      },
      {
        key: 'grossRevenue',
        label: 'Gross Revenue',
        className: 'detail-cell',
        disableNormalizerOnCopy: true,
        normalizer: value =>
          currencyNormalizerCreator(getActiveCurrencySymbol(event))(
            createFixedFloatNormalizer(2)(parseFloat(value)),
          ),
      },
      {
        key: 'grossRevenueTotal',
        label: makeRunningTotalLabel('Gross Revenue').value,
        labelComponent: makeRunningTotalLabel('Gross Revenue').component,
        className: 'detail-cell',
        disableNormalizerOnCopy: true,
        normalizer: value =>
          currencyNormalizerCreator(getActiveCurrencySymbol(event))(
            createFixedFloatNormalizer(2)(parseFloat(value)),
          ),
      },
    ])
  }

  getFooterColumns(event) {
    return [
      { key: 'numSales', sourceKey: 'numSales' },
      {
        key: 'revenue',
        sourceKey: 'revenue',
        normalizer: value =>
          currencyNormalizerCreator(getActiveCurrencySymbol(event))(
            createFixedFloatNormalizer(2)(parseFloat(value)),
          ),
      },
      {
        key: 'grossRevenue',
        sourceKey: 'grossRevenue',
        normalizer: value =>
          currencyNormalizerCreator(getActiveCurrencySymbol(event))(
            createFixedFloatNormalizer(2)(parseFloat(value)),
          ),
      },
    ]
  }

  updateTableColumns() {
    const { event } = this.props

    this.tableColumns = this.getMainTableColumns(event)
    this.detailsTableColumns = this.getDetailsTableColumns(event)
    this.footbarColumns = this.getFooterColumns(event)
  }

  componentDidMount() {
    this.updateData()
  }

  componentDidUpdate(prevProps, prevState) {
    const { data, event } = this.props

    if (!_isEqual(_get(event, 'selectedCurrency'), _get(prevProps.event, 'selectedCurrency'))) {
      this.updateTableColumns()
    }

    if (!_isEqual(data, prevProps.data)) {
      this.updateData()
    }
  }

  updateData = () => {
    const { data, groupedData } = this.props

    const detailsRows = []
    const rows = []

    this.getRows(data, groupedData, rows, detailsRows)
    this.setState({ rows, detailsRows })
  }

  getRows = (data, groupedData, rows, detailsRows) => {
    _map(groupedData, (value, key) => {
      const numSales = _reduce(value, (sum, n) => sum + parseInt(n.numSales), 0)
      const revenue = _reduce(
        value,
        (sum, n) => sum + parseInt(n.numSales) * parseFloat(n.cost) - parseFloat(n.refunded_amount),
        0,
      )
      const grossRevenue = _reduce(
        value,
        (sum, n) => sum + parseInt(n.numSales) * parseFloat(n.price) - parseFloat(n.refunded_amount),
        0,
      )
      this.getDetailRows(value, key, detailsRows)

      const updatedObj = {
        id: key,
        discountCode: value[0].discountCode,
        ticketType: value[0].ticketType,
        cost: value[0].cost,
        numSales,
        revenue,
        grossRevenue,
      }

      rows.push(updatedObj)
    })
  }

  getDetailRows = (data, groupId, detailsRows) => {
    const detailRows = []

    _map(_sortBy(data, 'datetime'), item => {
      const id = item.discountCode + item.ticketType
      if (id === groupId) {
        detailRows.push({
          id,
          datetime: item.datetime,
          numSales: parseInt(item.numSales),
          cost: item.cost,
          revenue: parseInt(item.numSales) * parseFloat(item.cost) - parseFloat(item.refunded_amount),
          discountedAmount:
            (parseFloat(item.originalPrice) - parseFloat(item.cost)) * parseInt(item.numSales),
          grossRevenue: parseInt(item.numSales) * parseFloat(item.price) - parseFloat(item.refunded_amount),
        })
      }
    })

    !!detailRows.length &&
      detailsRows.push({
        id: detailRows[0].id,
        type: 'detailRow',
        component: ({ detailRowIndex }) => (
          <SortableTable
            e2e_test_id={`sales_by_promo_code-${detailRowIndex}`}
            data={detailRows || []}
            tableColumns={this.detailsTableColumns}
            enableSort={false}
            enableCopyTable={true}
            disableMobileView={true}
            className="child-table"
            calculatedColumns={[
              { for: 'numSalesTotal', column: 'numSales' },
              { for: 'revenueTotal', column: 'revenue' },
              { for: 'grossRevenueTotal', column: 'grossRevenue' },
            ]}
          />
        ),
      })
  }

  render() {
    const { rows, sortBy, detailsRows } = this.state

    return (
      <div>
        {rows && rows.length ? (
          <SortableTable
            e2e_test_id="sales_by_promo_code"
            data={rows}
            tableColumns={this.tableColumns}
            enableSort={true}
            enableCopyTable={true}
            disableMobileView={true}
            sortBy={sortBy}
            detailsRows={detailsRows}
            footbar={{
              label: 'Total',
              columns: this.footbarColumns,
            }}
            scrollToRowEnabled={true}
          />
        ) : null}
      </div>
    )
  }
}
